import React, { useState, useEffect, useMemo } from "react";
import { Badge, Button, Col, Form, Image, InputGroup, Modal, Row, Table, Nav, Alert, Spinner, Card } from "react-bootstrap";
import { oneKlicks } from '../../SDK/oneclick';
import ImageShow from '../../Uitiles/ImageShow';
import { toast } from 'react-toastify';
import AddAttendanceModal from "./Components/AddAttendanceModal";

const AttendanceManagement = () => {
    const [search, setSearch] = useState("");
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // Format: YYYY-MM
    const [viewMode, setViewMode] = useState('day'); // 'day' or 'month'
    const [openAdd, setOpenAdd] = useState(false);
    const [attendanceList, setAttendanceList] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingAttendance, setEditingAttendance] = useState(null);

    useEffect(() => {
        fetchEmployeeList();
        fetchAttendanceList();
    }, [selectedDate, selectedMonth, viewMode]);

    const fetchEmployeeList = async () => {
        try {
            const tableSchema = '';
            const tableName = 'employees';
            const conditions = {
                status: 'active'
            };
            const orderBy = 'id DESC';
            const search = {};
            const pagination = {};
            const margedata = [
                {
                    target_table: 'users',
                    target_column: 'id',
                    target_value: 'staff_id',
                    target_label: 'staff'
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            
            if (response.success) {
                setEmployeeList(response.data);
            }
        } catch (error) {
            console.error('Error fetching employee list:', error);
        }
    };

    const fetchAttendanceList = async () => {
        setLoading(true);
        try {
            const tableSchema = '';
            const tableName = 'attendance';
            
            // Build conditions based on view mode

            // "conditions": {
            //     "date": { 
            //     "start": "2025-10-01", 
            //     "end": "2025-10-31" 
            //     }
            // }
            let conditions = {};
            if (viewMode === 'day') {
                conditions = { date: selectedDate };
            } else {
                // For month view, we need to filter by month
                // Assuming date format is 'YYYY-MM-DD', we'll use LIKE to match the month
                const startDate = `${selectedMonth}-01`;
                const endDate = `${selectedMonth}-31`;
                conditions = {
                    "date": { "start": startDate, "end": endDate}  //{ "start": 20, "end": 30 },
                };
            }
            
            const orderBy = 'date DESC, id DESC';
            const search = {};
            const pagination = {};
            const margedata = [
                {
                    target_table: 'employees',
                    target_column: 'id',
                    target_value: 'employee_id',
                    target_label: 'employee',
                    margedata: [
                        {
                            target_table: 'users',
                            target_column: 'id',
                            target_value: 'staff_id',
                            target_label: 'staff'
                        }
                    ]
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            
            if (response.success) {
                setAttendanceList(response.data);
            } else {
                toast.error('Failed to fetch attendance list');
            }
        } catch (error) {
            console.error('Error fetching attendance list:', error);
            toast.error('Error fetching attendance list');
        } finally {
            setLoading(false);
        }
    };

    const handleAddAttendance = () => {
        setEditingAttendance(null);
        setOpenAdd(true);
    };

    const handleEditAttendance = (attendance) => {
        setEditingAttendance(attendance);
        setOpenAdd(true);
    };

    const handleDeleteAttendance = async (attendanceId) => {
        if (window.confirm('Are you sure you want to delete this attendance record?')) {
            try {
                const tableSchema = '';
                const tableName = 'attendance';
                const conditions = { id: attendanceId };
                
                const response = await oneKlicks.delete(tableSchema, tableName, conditions);
                
                if (response.success) {
                    toast.success('Attendance record deleted successfully');
                    fetchAttendanceList();
                } else {
                    toast.error('Failed to delete attendance record');
                }
            } catch (error) {
                console.error('Error deleting attendance:', error);
                toast.error('Error deleting attendance record');
            }
        }
    };

    const handleAttendanceSuccess = () => {
        fetchAttendanceList();
        setOpenAdd(false);
    };

    const filteredAttendance = useMemo(() => {
        if (!search.trim()) return attendanceList;
        
        return attendanceList.filter(attendance => {
            const employeeName = `${attendance.employee?.[0]?.staff?.[0]?.first_name || ''} ${attendance.employee?.[0]?.staff?.[0]?.last_name || ''}`.toLowerCase();
            const status = attendance.status?.toLowerCase() || '';
            
            const searchTerm = search.toLowerCase();
            
            return employeeName.includes(searchTerm) || 
                   status.includes(searchTerm);
        });
    }, [attendanceList, search]);

    const getStatusBadge = (status) => {
        const statusConfig = {
            present: { variant: 'success', text: 'Present', icon: '✓' },
            absent: { variant: 'danger', text: 'Absent', icon: '✗' },
            late: { variant: 'warning', text: 'Late', icon: '⏰' },
            half_day: { variant: 'info', text: 'Half Day', icon: '½' },
            leave: { variant: 'secondary', text: 'Leave', icon: '🏖' },
            holiday: { variant: 'primary', text: 'Holiday', icon: '🎉' }
        };
        
        const config = statusConfig[status] || statusConfig.present;
        return (
            <Badge bg={config.variant}>
                {config.icon} {config.text}
            </Badge>
        );
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        return timeString;
    };

    const formatHours = (hours) => {
        if (!hours || hours === '0') return 'N/A';
        return `${hours}h`;
    };

    const calculateAttendanceStats = () => {
        const totalEmployees = employeeList.length;
        const presentCount = attendanceList.filter(a => a.status === 'present').length;
        const absentCount = attendanceList.filter(a => a.status === 'absent').length;
        const lateCount = attendanceList.filter(a => a.status === 'late').length;
        const leaveCount = attendanceList.filter(a => a.status === 'leave').length;
        const attendancePercentage = totalEmployees > 0 ? ((presentCount / totalEmployees) * 100).toFixed(1) : 0;

        return {
            totalEmployees,
            presentCount,
            absentCount,
            lateCount,
            leaveCount,
            attendancePercentage
        };
    };

    const stats = calculateAttendanceStats();

    return (
        <div className="dc-haslayout dc-dbsectionspace">
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3">Attendance Management</h5>
                                <Badge bg="info">HRM System</Badge>
                            </div>
                            
                            {/* View Mode Tabs */}
                            <Nav variant="pills" className="mb-3">
                                <Nav.Item>
                                    <Nav.Link 
                                        active={viewMode === 'day'} 
                                        onClick={() => setViewMode('day')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        📅 Day View
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link 
                                        active={viewMode === 'month'} 
                                        onClick={() => setViewMode('month')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        📆 Month View
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>

                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="p-0" style={{ maxWidth: 400, flex: "1 1 auto" }}>
                                    <Form.Control
                                        placeholder="Search attendance..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </InputGroup>
                                
                                {viewMode === 'day' ? (
                                    <Form.Control
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        style={{ maxWidth: 200 }}
                                    />
                                ) : (
                                    <Form.Control
                                        type="month"
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(e.target.value)}
                                        style={{ maxWidth: 200 }}
                                    />
                                )}
                                
                                <Button variant="primary" onClick={handleAddAttendance}>+ Add Attendance</Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                <Row className="mb-4">
                    <Col md={2}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-primary">{stats.totalEmployees}</h3>
                                <p className="mb-0">Total Employees</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-success">{stats.presentCount}</h3>
                                <p className="mb-0">Present</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-danger">{stats.absentCount}</h3>
                                <p className="mb-0">Absent</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-warning">{stats.lateCount}</h3>
                                <p className="mb-0">Late</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-secondary">{stats.leaveCount}</h3>
                                <p className="mb-0">On Leave</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-info">{stats.attendancePercentage}%</h3>
                                <p className="mb-0">Attendance Rate</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p className="mt-2">Loading attendance records...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="mb-0 align-middle">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            {viewMode === 'month' && <th>Date</th>}
                                            <th>Employee</th>
                                            <th>Status</th>
                                            <th>Check In</th>
                                            <th>Check Out</th>
                                            <th>Total Hours</th>
                                            <th>Overtime</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAttendance.length > 0 ? (
                                            filteredAttendance.map((attendance, index) => (
                                                <tr key={attendance.id}>
                                                    <td data-label="ID">{index + 1}</td>
                                                    {viewMode === 'month' && (
                                                        <td data-label="Date">
                                                            <span className="fw-bold">
                                                                {new Date(attendance.date).toLocaleDateString('en-US', { 
                                                                    month: 'short', 
                                                                    day: 'numeric',
                                                                    weekday: 'short'
                                                                })}
                                                            </span>
                                                        </td>
                                                    )}
                                                    <td data-label="Employee">
                                                        <div className="d-flex align-items-center">
                                                            <ImageShow 
                                                                url={attendance.employee?.[0]?.staff?.[0]?.profile_photo_url} 
                                                                width={40} 
                                                                height={40}
                                                                style={{ 
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '50%', 
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                            <div width="100%" style={{ marginLeft: 10 }}>
                                                                <h6 className="fw-bold" style={{ maxWidth: 150 , height: '23px'}}>
                                                                    {attendance.employee?.[0]?.staff?.[0]?.first_name} {attendance.employee?.[0]?.staff?.[0]?.last_name}
                                                                </h6>
                                                                <small className="text-muted">
                                                                    {attendance.employee?.[0]?.employee_id} - {attendance.employee?.[0]?.department}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Status">{getStatusBadge(attendance.status)}</td>
                                                    <td data-label="Check In">
                                                        <span className="fw-bold text-success">
                                                            {formatTime(attendance.check_in)}
                                                        </span>
                                                    </td>
                                                    <td data-label="Check Out">
                                                        <span className="fw-bold text-primary">
                                                            {formatTime(attendance.check_out)}
                                                        </span>
                                                    </td>
                                                    <td data-label="Total Hours">
                                                        <span className="fw-bold">
                                                            {formatHours(attendance.total_hours)}
                                                        </span>
                                                    </td>
                                                    <td data-label="Overtime">
                                                        <span className="fw-bold text-warning">
                                                            {formatHours(attendance.overtime_hours)}
                                                        </span>
                                                    </td>
                                                    <td data-label="Actions"    >
                                                        <div className="d-flex gap-1">
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                onClick={() => handleEditAttendance(attendance)}
                                                                title="View Attendance"
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </Button>
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                onClick={() => handleEditAttendance(attendance)}
                                                                title="Edit Attendance"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </Button>
                                                            <Button 
                                                                variant="outline-danger" 
                                                                size="sm"
                                                                onClick={() => handleDeleteAttendance(attendance.id)}
                                                                title="Delete Attendance"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={viewMode === 'month' ? "9" : "8"} className="text-center py-4">
                                                    <Alert variant="info">
                                                        No attendance records found for {viewMode === 'day' ? selectedDate : selectedMonth}. 
                                                        {search && ' Try adjusting your search criteria.'}
                                                        {!search && ' Click "Add Attendance" to record attendance.'}
                                                    </Alert>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <AddAttendanceModal
                show={openAdd} 
                onHide={() => setOpenAdd(false)}
                onSuccess={handleAttendanceSuccess}
                editingAttendance={editingAttendance}
                selectedDate={selectedDate}
            />
        </div>
    );
};

export default AttendanceManagement;
